/**
 * WebLogger - Per-component logger for web apps
 *
 * Usage:
 *   const logger = new WebLogger(WebComponentCode.WEB_API, 'fetchQuestions');
 *   logger.info('Loaded 5 questions', { count: 5 });
 *   logger.error('Failed to fetch', err);
 */

import {
  LogLevel,
  LogLevelName,
  WebComponentCode,
  WebComponentName,
  type WebLogEntry,
  type WebErrorDetail,
} from './types';
import { getWebTracingManager } from './WebTracingManager';

export class WebLogger {
  private component: WebComponentCode;
  private componentName: string;
  private operation?: string;

  constructor(component: WebComponentCode, operation?: string) {
    this.component = component;
    this.componentName = WebComponentName[component] || `Component ${component}`;
    this.operation = operation;
  }

  /** Create a child logger with a different operation */
  child(operation: string): WebLogger {
    return new WebLogger(this.component, operation);
  }

  error(message: string, error?: unknown, data?: Record<string, unknown>): void {
    this.log(LogLevel.ERROR, message, data, this.toErrorDetail(error));
  }

  warn(message: string, data?: Record<string, unknown>): void {
    this.log(LogLevel.WARN, message, data);
  }

  info(message: string, data?: Record<string, unknown>): void {
    this.log(LogLevel.INFO1, message, data);
  }

  info2(message: string, data?: Record<string, unknown>): void {
    this.log(LogLevel.INFO2, message, data);
  }

  info3(message: string, data?: Record<string, unknown>): void {
    this.log(LogLevel.INFO3, message, data);
  }

  debug(message: string, data?: Record<string, unknown>): void {
    this.log(LogLevel.DEBUG, message, data);
  }

  /** Start a timer, returns a function that logs elapsed time */
  startTimer(message: string): () => void {
    const start = performance.now();
    return () => {
      const duration = Math.round(performance.now() - start);
      this.info2(`${message} (${duration}ms)`, { duration });
    };
  }

  private log(level: LogLevel, message: string, data?: Record<string, unknown>, error?: WebErrorDetail): void {
    const manager = getWebTracingManager();

    if (!manager.isLevelEnabled(level, this.component)) return;

    const entry: WebLogEntry = {
      sessionId: manager.sessionId,
      level,
      levelName: LogLevelName[level] || 'Unknown',
      component: this.component,
      componentName: this.componentName,
      operation: this.operation,
      message,
      data,
      error,
      source: manager.source,
      timestamp: new Date().toISOString(),
      url: typeof window !== 'undefined' ? window.location.pathname : undefined,
    };

    manager.log(entry);
  }

  private toErrorDetail(error: unknown): WebErrorDetail | undefined {
    if (!error) return undefined;

    if (error instanceof Error) {
      return {
        name: error.name,
        message: error.message,
        stack: error.stack,
      };
    }

    if (typeof error === 'string') {
      return { name: 'Error', message: error };
    }

    return { name: 'Error', message: String(error) };
  }
}
